import { useMemo, useState } from "react";
import { scaleLinear, scaleTime } from "d3-scale";
import { TrendingUp } from "lucide-react";

const WIDTH = 900;
const HEIGHT = 260;
const MARGIN = { top: 20, right: 20, bottom: 32, left: 40 };
const INNER_W = WIDTH - MARGIN.left - MARGIN.right;
const INNER_H = HEIGHT - MARGIN.top - MARGIN.bottom;

const RANGES = [
  { key: "7", label: "7g", days: 7 },
  { key: "30", label: "30g", days: 30 },
  { key: "all", label: "Tutto", days: null },
];

function toDayKey(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatShort(d) {
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}

export default function GrowthChart({ entries }) {
  const [rangeKey, setRangeKey] = useState("30");
  const [hoverIdx, setHoverIdx] = useState(null);

  const { series, totalNew, peakDay } = useMemo(() => {
    if (!entries || entries.length === 0) {
      return { series: [], totalNew: 0, peakDay: null };
    }

    // Parse and sort dates ascending
    const parsed = entries
      .map((e) => new Date(e.created_at))
      .filter((d) => !isNaN(d))
      .sort((a, b) => a - b);
    if (parsed.length === 0) return { series: [], totalNew: 0, peakDay: null };

    const range = RANGES.find((r) => r.key === rangeKey);
    const now = new Date();
    const cutoff = range.days
      ? new Date(now.getTime() - range.days * 24 * 60 * 60 * 1000)
      : parsed[0];

    // Cumulative count = number of entries at or before day D
    // For chart: we compute per-day cumulative starting from earliest entry
    // Then slice to the visible range
    const dayMap = new Map();
    parsed.forEach((d) => {
      const k = toDayKey(d);
      dayMap.set(k, (dayMap.get(k) || 0) + 1);
    });

    // Build full day range from cutoff to today
    const start = new Date(
      Date.UTC(cutoff.getUTCFullYear(), cutoff.getUTCMonth(), cutoff.getUTCDate())
    );
    const end = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );

    // Compute cumulative count up to start day (all entries before cutoff)
    let cumulative = parsed.filter((d) => d < start).length;

    const days = [];
    for (let t = start.getTime(); t <= end.getTime(); t += 24 * 60 * 60 * 1000) {
      const d = new Date(t);
      const k = toDayKey(d);
      const dayCount = dayMap.get(k) || 0;
      cumulative += dayCount;
      days.push({ date: d, cumulative, dayCount });
    }

    const totalNew = days.reduce((sum, d) => sum + d.dayCount, 0);
    const peakDay = days.reduce(
      (best, d) => (d.dayCount > (best?.dayCount || 0) ? d : best),
      null
    );

    return { series: days, totalNew, peakDay };
  }, [entries, rangeKey]);

  if (series.length === 0) {
    return (
      <div
        data-testid="admin-growth-empty"
        className="rounded-2xl border border-white/[0.08] bg-[#0D111A] p-6"
      >
        <div className="flex items-center gap-3">
          <TrendingUp size={16} className="text-[#00F562]" />
          <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
            Crescita iscritti
          </h2>
        </div>
        <p className="mt-8 pb-4 text-center font-mono text-xs uppercase tracking-widest text-slate-500">
          Nessun iscritto ancora — il grafico apparirà dopo la prima iscrizione.
        </p>
      </div>
    );
  }

  const x = scaleTime()
    .domain([series[0].date, series[series.length - 1].date])
    .range([0, INNER_W]);
  const maxY = Math.max(1, ...series.map((d) => d.cumulative));
  const y = scaleLinear().domain([0, maxY * 1.1]).range([INNER_H, 0]).nice();

  const linePath = series
    .map((d, i) => `${i === 0 ? "M" : "L"} ${x(d.date)} ${y(d.cumulative)}`)
    .join(" ");
  const areaPath =
    `M ${x(series[0].date)} ${INNER_H} ` +
    series.map((d) => `L ${x(d.date)} ${y(d.cumulative)}`).join(" ") +
    ` L ${x(series[series.length - 1].date)} ${INNER_H} Z`;

  // X ticks
  const numTicks = Math.min(6, series.length);
  const step = Math.max(1, Math.floor(series.length / numTicks));
  const xTicks = series.filter((_, i) => i % step === 0);

  // Y ticks
  const yTickValues = y.ticks(4);

  const finalTotal = series[series.length - 1].cumulative;

  return (
    <div
      data-testid="admin-growth-chart"
      className="rounded-2xl border border-white/[0.08] bg-[#0D111A] p-6"
    >
      <div className="flex flex-wrap items-center gap-3">
        <TrendingUp size={16} className="text-[#00F562]" />
        <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
          Crescita iscritti
        </h2>
        <div className="ml-auto flex items-center gap-1 rounded-full border border-white/10 bg-[#06080C] p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`growth-range-${r.key}`}
              type="button"
              onClick={() => setRangeKey(r.key)}
              className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors duration-200 ${
                rangeKey === r.key
                  ? "bg-[#00F562] text-[#06080C]"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-white/[0.06] bg-[#141A26]/60 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Totale
          </p>
          <p className="mt-1 font-mono text-xl font-bold text-[#00F562] tabular-nums">
            {finalTotal}
          </p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-[#141A26]/60 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Nuovi (range)
          </p>
          <p className="mt-1 font-mono text-xl font-bold text-white tabular-nums">
            +{totalNew}
          </p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-[#141A26]/60 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Picco
          </p>
          <p className="mt-1 font-mono text-sm font-bold text-white tabular-nums">
            {peakDay ? `+${peakDay.dayCount}` : "—"}
            {peakDay && peakDay.dayCount > 0 && (
              <span className="ml-2 text-[10px] font-normal uppercase tracking-widest text-slate-500">
                {formatShort(peakDay.date)}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mt-5 overflow-hidden">
        <svg
          data-testid="growth-chart-svg"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full"
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#00F562" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#00F562" stopOpacity="0" />
            </linearGradient>
          </defs>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {/* Grid Y */}
            {yTickValues.map((v) => (
              <g key={v}>
                <line
                  x1={0}
                  x2={INNER_W}
                  y1={y(v)}
                  y2={y(v)}
                  stroke="#1E293B"
                  strokeDasharray="2 4"
                />
                <text
                  x={-8}
                  y={y(v)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="#475569"
                  fontFamily="ui-monospace, monospace"
                  fontSize="10"
                >
                  {v}
                </text>
              </g>
            ))}

            {/* X ticks */}
            {xTicks.map((d, i) => (
              <text
                key={i}
                x={x(d.date)}
                y={INNER_H + 18}
                textAnchor="middle"
                fill="#475569"
                fontFamily="ui-monospace, monospace"
                fontSize="10"
              >
                {formatShort(d.date)}
              </text>
            ))}

            {/* Area */}
            <path d={areaPath} fill="url(#areaGrad)" />
            {/* Line */}
            <path
              d={linePath}
              fill="none"
              stroke="#00F562"
              strokeWidth={2}
              strokeLinejoin="round"
            />

            {/* Data points + hover */}
            {series.map((d, i) => (
              <g key={i}>
                <circle
                  cx={x(d.date)}
                  cy={y(d.cumulative)}
                  r={hoverIdx === i ? 5 : 3}
                  fill={d.dayCount > 0 ? "#00F562" : "#0D111A"}
                  stroke="#00F562"
                  strokeWidth={hoverIdx === i ? 2 : 1}
                  style={{ transition: "r 150ms" }}
                />
                <rect
                  x={x(d.date) - 12}
                  y={0}
                  width={24}
                  height={INNER_H}
                  fill="transparent"
                  onMouseEnter={() => setHoverIdx(i)}
                />
              </g>
            ))}

            {/* Hover tooltip */}
            {hoverIdx !== null && (
              <g>
                <line
                  x1={x(series[hoverIdx].date)}
                  x2={x(series[hoverIdx].date)}
                  y1={0}
                  y2={INNER_H}
                  stroke="#00F562"
                  strokeOpacity="0.3"
                  strokeDasharray="3 3"
                />
                <g
                  transform={`translate(${Math.min(
                    x(series[hoverIdx].date) + 10,
                    INNER_W - 140
                  )}, ${Math.max(y(series[hoverIdx].cumulative) - 40, 0)})`}
                >
                  <rect
                    x={0}
                    y={0}
                    width={140}
                    height={54}
                    rx={8}
                    fill="#06080C"
                    stroke="#00F562"
                    strokeOpacity="0.4"
                  />
                  <text
                    x={12}
                    y={18}
                    fill="#94A3B8"
                    fontFamily="ui-monospace, monospace"
                    fontSize="10"
                    style={{ textTransform: "uppercase", letterSpacing: 2 }}
                  >
                    {formatShort(series[hoverIdx].date)}
                  </text>
                  <text
                    x={12}
                    y={40}
                    fill="#FFFFFF"
                    fontFamily="ui-monospace, monospace"
                    fontSize="14"
                    fontWeight="700"
                  >
                    {series[hoverIdx].cumulative} tot.
                  </text>
                  <text
                    x={128}
                    y={40}
                    textAnchor="end"
                    fill="#00F562"
                    fontFamily="ui-monospace, monospace"
                    fontSize="12"
                    fontWeight="700"
                  >
                    +{series[hoverIdx].dayCount}
                  </text>
                </g>
              </g>
            )}
          </g>
        </svg>
      </div>
    </div>
  );
}
